import { Add, CreateOutlined, Lock } from "@mui/icons-material";
import { useStripe } from "@stripe/react-stripe-js";
import axios from "axios";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { Sheet } from "react-modal-sheet";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../../config";
import { collectionPath, EnvContext } from "../../context/EnvContext";
import SessionReplayContext from "../../context/SessionReplayContext";
import { UserContext } from "../../context/UserContext";
import AddressForm from "../../elements/AddressForm";
import CustomCardElement from "../../elements/CustomCardElement.jsx";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import Header from "../../elements/Header";
import ShippingCheckoutBanner from "../../elements/ShippingCheckoutBanner";
import ShippingOptions from "../../elements/ShippingOptions";
import Summary from "../../elements/Summary";
import { useFirestore } from "../../firebase/FirestoreContext";
import noop from "../../functions/noop";
import useCart from "../../hooks/useCart";
import useCartValidations from "../../hooks/useCartValidations";
import analyticsHelper from "../../utils/analyticsHelper";
import { toStripeAddress } from "../../utils/format.js";
import FPButton from "../../elements/FPButton.jsx";
import { getCurrentFacebookClickId } from "../../utils/facebookClickId";
import apiClient from "../../utils/ApiClient";
import SavedPaymentMethod from "../../elements/SavedPaymentMethod.jsx";
import AddressBox from "../../elements/AddressBox.jsx";
import StickyFooter from "../../elements/StickyFooter.jsx";
import { GridLoader } from "react-spinners";
import ExpressCheckout from "../../elements/ExpressCheckout.jsx";
import CheckoutAuthBanner from "../../elements/CheckoutAuthBanner.jsx";
import styled from "@emotion/styled";

const HelpText = styled.span`
  color: gray;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;

  svg {
    width: 18px;
  }
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Checkout = () => {
  const { validateCart } = useCartValidations();
  const {
    summary,
    appliedDiscount,
    cartItems,
    wallet,
    cart,
    refresh,
    refreshedAtTime,
    selectedShippingData,
    setSelectedShippingData,
  } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const db = useFirestore();
  const firestore = useFirestore();
  const { user, loading } = React.useContext(UserContext);
  const environment = useContext(EnvContext);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [hasFetchedPaymentMethods, setHasFetchedPaymentMethods] =
    useState(false);

  const [shippingAddress, setShippingAddress] = useState();
  const [shippingAddressValid, setShippingAddressValid] = useState(false);

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState(null);
  const [billingAddressValid, setBillingAddressValid] = useState(false);

  const [cardData, setCardData] = useState({ name: "" });
  const [cardError, setCardError] = useState(null);
  const [mountedCardElements, setMountedCardElements] = useState({
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
  });

  // Saved payment methods state
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // null for new card, payment method id for saved card
  const [savePaymentMethod, setSavePaymentMethod] = useState(
    () => !!user?.email,
  );
  const [loadingSavedMethods, setLoadingSavedMethods] = useState(false);
  const [showStickyFooter, setShowStickyFooter] = useState(true);
  const payButtonRef = useRef(null);

  const selectedAddress = shippingAddressValid
    ? shippingAddress
    : addresses[selectedAddressIndex];

  const { enableSessionReplay } = useContext(SessionReplayContext);

  const stripe = useStripe();

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [checkoutData, setCheckoutData] = useState({
    cartID: null,
    cartSooperstockID: null,
    items: [],
    subtotal: 0,
    shippingCost: 0,
    freeShippingThreshold: 0,
    appliedDiscount: {
      discountAmount: 0,
      percentage: 0,
      name: "",
    },
    walletBalance: 0,
    minPaymentAmount: 0,
    finalAmount: 0,
    perishableAmount: 0,
  });

  const { cart_sooperstock_ID = null } = location.state || {};

  const { id: cartID } = cart || {};

  const [isOpen, setOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [tempSelectedAddressIndex, setTempSelectedAddressIndex] = useState(0);

  const handleAddressSelect = (index) => {
    setTempSelectedAddressIndex(index);
  };

  const handleSaveAddressChange = () => {
    setSelectedAddressIndex(tempSelectedAddressIndex);

    if (tempSelectedAddressIndex !== 0) {
      const updatedAddresses = [...addresses];
      const selectedAddress = updatedAddresses[tempSelectedAddressIndex];
      updatedAddresses.splice(tempSelectedAddressIndex, 1);
      updatedAddresses.unshift(selectedAddress);
      setAddresses(updatedAddresses);
      // Since we moved the address to the top, update the index to 0
      setSelectedAddressIndex(0);
    }

    setOpen(false);
  };

  const handleAddNewAddress = () => {
    setOpen(false);
    setShowAddNewForm(true);
  };

  const toggleAddressOptions = () => {
    setTempSelectedAddressIndex(selectedAddressIndex);
    setOpen(true);
  };

  useEffect(() => {
    function checkPayVisibility() {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener("scroll", checkPayVisibility, { passive: true });
    window.addEventListener("resize", checkPayVisibility);

    const timeoutId = setTimeout(checkPayVisibility, 50);

    return () => {
      window.removeEventListener("scroll", checkPayVisibility);
      window.removeEventListener("resize", checkPayVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [summary, cartItems.length, isLoading, isCheckoutLoading]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsLoading(false);
        setIsCheckoutLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const saveAddress = async (address) => {
    const updatedAddresses = [address, ...addresses];
    setAddresses(updatedAddresses);
    setSelectedAddressIndex(0);

    analyticsHelper.identify(
      { ...user, email: user.email || address.email },
      () => analyticsHelper.addedAddress(address),
    );

    const data = { addresses: updatedAddresses };
    await updateDoc(doc(firestore, "Users/UsersList/DataBase", user.uid), data);

    // Only hide form for authenticated users with email
    if (user?.email) {
      setShowAddNewForm(false);
    }
  };

  // Fetch saved payment methods for the current user
  const fetchSavedPaymentMethods = async () => {
    if (!user || !user.stripeCustomerId) return;

    setLoadingSavedMethods(true);
    try {
      const data = await apiClient.getData("/stripe/payment-methods", user);
      const paymentMethods = data.paymentMethods || [];
      setSavedPaymentMethods(paymentMethods);
      // Set first payment method as default if available
      if (paymentMethods.length > 0) {
        setSelectedPaymentMethod(paymentMethods[0].id);
      }
      setHasFetchedPaymentMethods(true);
    } catch (error) {
      console.error("Error fetching saved payment methods:", error);
    } finally {
      setLoadingSavedMethods(false);
    }
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (paymentMethodId) => {
    setSelectedPaymentMethod(paymentMethodId);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await fetchCheckoutData();
        setHasFetchedData(true);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only load data once when conditions are met and data hasn't been fetched yet
    if (!loading && !hasFetchedData && (user?.uid || !user) && cartID) {
      loadData();
    }
  }, [user?.uid, cartID, firestore, collectionPath, loading, hasFetchedData]);

  useEffect(() => {
    if (isMounted) return noop;

    if (cartItems.length > 0 && summary.total > 0.01 && wallet) {
      analyticsHelper.startedCheckout({
        cartItems,
        summary,
        cart_sooperstock_ID,
        wallet,
      });
      setIsMounted(true);
    }
  }, [isMounted, cartItems.length, cart_sooperstock_ID, summary.total, wallet]);

  const fetchCheckoutData = async () => {
    try {
      if (!user || !cartID) return;

      const cartDocRef = doc(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartID,
      );
      const cartSnapshot = await getDoc(cartDocRef);
      const cartData = cartSnapshot.exists() ? cartSnapshot.data() : {};
      await updateDoc(cartDocRef, {
        opening_checkout_page: new Date(), // Firebase server timestamp
      });

      const walletCollection = collection(
        firestore,
        `${collectionPath}Wallets/UserWallets/DataBase`,
      );
      const walletQuery = query(
        walletCollection,
        where("user_uid", "==", user.uid),
      );
      const walletSnapshot = await getDocs(walletQuery);
      const totalWalletBalance = walletSnapshot.docs.reduce(
        (acc, doc) => acc + (doc.data().wallet_value || 0),
        0,
      );

      const settingsDocRef = doc(
        firestore,
        `${collectionPath}SooperstockSettings`,
        "Wallet",
      );
      const settingsDoc = await getDoc(settingsDocRef);
      const minWalletPayment = settingsDoc.exists()
        ? settingsDoc.data().minWalletPayment / 100
        : 0;

      const shipmentDocRef = doc(
        firestore,
        `${collectionPath}SooperstockSettings`,
        "Shipment",
      );
      const shipmentDoc = await getDoc(shipmentDocRef);
      const shipmentData = shipmentDoc.exists() ? shipmentDoc.data() : {};

      setCheckoutData({
        cartID,
        cartSooperstockID: cartData.cart_sooperstock_ID || null,
        items: cartData.items || [],
        subtotal: cartData.subtotal || 0,
        shippingCost: cartData.shippingCost || 0,
        freeShippingThreshold: shipmentData.freeShipmentThreshold || 0,
        appliedDiscount: {
          discountAmount: cartData.applied_discount_amount || 0,
          percentage: cartData.applied_discount_percentage || 0,
          name: cartData.applied_discount_name || "",
        },
        walletBalance: totalWalletBalance,
        minPaymentAmount: minWalletPayment,
        finalAmount: cartData.finalAmount || 0,
        perishableAmount: cartData.perishableAmount || 0,
      });
    } catch (error) {
      console.error("Error fetching checkout data:", error);
    }
  };

  useEffect(() => {
    const fetchAddresses = () => {
      if (!loading && !user?.email) {
        setShowAddNewForm(true); // Always show form for anonymous users
      } else if (!loading && user?.addresses?.length) {
        setAddresses(user.addresses);
        setShowAddNewForm(false); // Hide form if addresses exist
      } else if (
        !loading &&
        (!user?.addresses || user.addresses.length === 0)
      ) {
        setShowAddNewForm(true); // Show form if no addresses
      }
    };

    // Only fetch addresses when loading is complete
    if (!loading) {
      fetchAddresses();
    }
  }, [loading, user?.email, user?.addresses]);

  // Fetch saved payment methods when user loads
  useEffect(() => {
    if (
      !loading &&
      !hasFetchedPaymentMethods &&
      user?.uid &&
      user.stripeCustomerId
    ) {
      fetchSavedPaymentMethods();
    }
  }, [loading, user?.uid, user?.stripeCustomerId, hasFetchedPaymentMethods]);

  const checkRepeatOrder = async () => {
    try {
      const currentEmail = user?.email || selectedAddress?.email;
      const payload = {};

      if (user?.uid) {
        payload.user_id = user.uid;
      }

      if (currentEmail) {
        payload.email = currentEmail;
      }

      if (selectedAddress) {
        payload.shippingAddress = {
          street: selectedAddress.addressLine1,
          street2: selectedAddress.addressLine2 || "",
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode,
        };
      }

      const response = await apiClient.postData(
        "/order/check-repeat",
        payload,
        user,
      );

      // Update cart with repeat order check result
      await updateDoc(
        doc(firestore, `${collectionPath}Carts/CartsList/DataBase`, cartID),
        {
          repeatOrderCheck: response,
          repeatOrderCheckTimestamp: new Date(),
        },
      );

      return response;
    } catch (error) {
      console.error("Error checking repeat order:", error);
      return null;
    }
  };

  const handleWalletOnlyPayment = async () => {
    setIsLoading(true);
    const cartErrors = await validateCart(cartID);

    if (cartErrors.length > 0) {
      navigate("/cart");
      return;
    }

    try {
      // Check if this is a repeat order
      await checkRepeatOrder();

      await saveShippingOption(selectedShippingData);

      if (summary.paymentTotals.wallet <= 0) {
        alert("Wallet deduction amount is invalid.");
      }

      let currentEmail = user.email;
      if (!currentEmail) {
        currentEmail = addresses[selectedAddressIndex]?.email;
      }

      if (!currentEmail) {
        alert("Please provide your address and email address to finish order.");
        setIsLoading(false);
        return;
      }

      const shipmentAddress = addresses[selectedAddressIndex];
      if (!shipmentAddress?.email) {
        shipmentAddress.email = currentEmail;
      }

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      await updateDoc(
        doc(
          firestore,
          `${collectionPath}Carts/CartsList/DataBase`,
          checkoutData.cartID,
        ),
        {
          paymentType: "Wallet",
          walletDeduction: summary.paymentTotals.wallet,
          shipmentAddress: shipmentAddress,
        },
      );

      const payload = {
        cartID: checkoutData.cartID,
        status: "PAID",
        paymentType: "Wallet",
        walletDeduction: summary.paymentTotals.wallet,
        environment,
      };

      const response = await axios.post(
        `${config.apiConfig.API_ENDPOINT_URL}/payment/walletWebhookPayment`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        navigate(`/payment-success?cart_id=${checkoutData.cartID}&goal=true`, {
          replace: true,
        });
      } else {
        throw new Error(response.data.error || "Order creation failed.");
      }

      navigate(`/payment-success?cart_id=${checkoutData.cartID}&goal=true`, {
        replace: true,
      });
    } catch (error) {
      console.error("Wallet payment error:", error);
      alert("Failed to process wallet-only payment.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardElementChange = (event) => {
    if (event && event.type === "name") {
      setCardData((prev) => ({ ...prev, name: event.value }));
      return;
    }

    if (event && event.error) {
      setCardError(event.error.message);
    } else {
      setCardError(null);
    }
  };

  const handleCardElementsReady = useCallback((elements) => {
    setMountedCardElements(elements);
  }, []);

  const handleExpressPaymentAuthorization = async ({
    paymentMethod,
    payerName,
    payerEmail,
    shippingAddress: expressShippingAddress,
    paymentType,
  }) => {
    try {
      const cartErrors = await validateCart(cartID);
      if (cartErrors.length > 0) {
        navigate("/cart");
        return { error: "Cart validation failed" };
      }

      // Check if this is a repeat order
      await checkRepeatOrder();

      await saveShippingOption(selectedShippingData);

      const facebookClickId = getCurrentFacebookClickId();
      const finalShippingAddress =
        selectedAddress ||
        (expressShippingAddress
          ? {
              name: payerName || "",
              email: payerEmail || user?.email || "",
              addressLine1: expressShippingAddress.addressLines?.[0] || "",
              addressLine2: expressShippingAddress.addressLines?.[1] || "",
              city: expressShippingAddress.locality || "",
              state: expressShippingAddress.administrativeArea || "",
              zipCode: expressShippingAddress.postalCode || "",
              country: expressShippingAddress.countryCode || "US",
              phone: expressShippingAddress.phoneNumber || "",
            }
          : null);

      if (!finalShippingAddress) {
        return { error: "Shipping address required" };
      }

      const paymentIntentData = {
        shippingAddress: finalShippingAddress,
        amount: Math.round(summary.paymentTotals.cc * 100),
        environment,
        setup_future_usage: savePaymentMethod ? "off_session" : undefined,
        payment_method: paymentMethod.id,
        shipping_cost: selectedShippingData?.shippingCost || 0,
        metadata: {
          cart_id: cartID,
          discount_code: appliedDiscount?.name || "No Discount Code",
          discount_amount: summary.discountAmount.toFixed(2),
          facebook_click_id: facebookClickId || undefined,
          payment_type: paymentType || "express_checkout",
        },
      };

      if (shippingAddress && shippingAddressValid && !user?.email) {
        await saveAddress(shippingAddress);
      }

      const data = await apiClient.postData(
        "/stripe/payment-intent",
        paymentIntentData,
        user,
      );
      if (!data.clientSecret) {
        return { error: data.error || "Could not create payment intent" };
      }

      await saveCartInfo();

      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: paymentMethod.id,
      });

      if (result.error) {
        return { error: result.error.message };
      }

      await saveCartInfo(result.paymentIntent);
      if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
        navigate(`/payment-success?cart_id=${cartID}&goal=true`, {
          replace: true,
        });
        return { success: true };
      }
      return { error: "Payment failed" };
    } catch (error) {
      console.error("Apple Pay error:", error);
      return { error: error.message || "Payment failed" };
    }
  };

  const handleStripePaymentIntent = async () => {
    setIsCheckoutLoading(true);

    const cartErrors = await validateCart(cartID);

    if (cartErrors.length > 0) {
      navigate("/cart");
      return;
    }

    try {
      // Check if this is a repeat order
      await checkRepeatOrder();
      await saveShippingOption(selectedShippingData);

      const { cardNumber, cardExpiry, cardCvc } = mountedCardElements;
      const address = billingSameAsShipping ? selectedAddress : billingAddress;

      // Only validate card elements if we're using a new payment method
      if (
        selectedPaymentMethod === null &&
        (!cardNumber || !cardExpiry || !cardCvc)
      ) {
        setCardError("Card fields are not ready. Please check your card info.");
        setIsCheckoutLoading(false);
        setIsLoading(false);
        alert("Card fields are not ready. Please check your card info.");
        return;
      }

      const token = user?.accessToken;
      if (!token) {
        setIsCheckoutLoading(false);
        setIsLoading(false);
        alert("User not authenticated.");
        return;
      }
      const facebookClickId = getCurrentFacebookClickId();
      const paymentIntentData = {
        shippingAddress: selectedAddress,
        amount: Math.round(summary.paymentTotals.cc * 100), // Cents
        environment,
        setup_future_usage: savePaymentMethod ? "off_session" : undefined,
        payment_method: selectedPaymentMethod,
        shipping_cost: selectedShippingData.shippingCost,
        free_shipping_threshold:
          selectedShippingData.shippingOption.free_threshold,
        metadata: {
          cart_id: cartID,
          discount_code: appliedDiscount?.name || "No Discount Code",
          discount_amount: summary.discountAmount.toFixed(2),
          facebook_click_id: facebookClickId || undefined,
        },
      };

      const data = await apiClient.postData(
        "/stripe/payment-intent",
        paymentIntentData,
        user,
      );
      if (!data.clientSecret) {
        setIsCheckoutLoading(false);
        setIsLoading(false);
        alert(data.error || "Could not create payment intent");
        return;
      }

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      const clientSecret = data.clientSecret;
      let confirmationParams;

      if (selectedPaymentMethod) {
        // Using saved payment method
        confirmationParams = {
          payment_method: selectedPaymentMethod,
        };
      } else {
        // Using new payment method
        confirmationParams = {
          payment_method: {
            card: cardNumber,
            billing_details: {
              name: cardData.name,
              email: user?.email || address.email,
              address: toStripeAddress(address),
            },
          },
        };
      }

      await saveCartInfo();

      const result = await stripe.confirmCardPayment(
        clientSecret,
        confirmationParams,
      );

      if (result.error) {
        setCardError(result.error.message);
        setIsCheckoutLoading(false);
        setIsLoading(false);
        return;
      }

      await saveCartInfo(result.paymentIntent);
      if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
        navigate(`/payment-success?cart_id=${cartID}&goal=true`, {
          replace: true,
        });
      }
      setIsCheckoutLoading(false);
      setIsLoading(false);
    } catch (error) {
      setIsCheckoutLoading(false);
      setIsLoading(false);
      setCardError("There was an issue with checkout. Try again later");
      console.error(error);
    }
  };

  const saveShippingOption = async (shippingData) => {
    const docRef = doc(db, `${collectionPath}Carts/CartsList/DataBase`, cartID);
    const data = {
      shippingOption: shippingData.shippingOption || null,
      shippingCost: shippingData?.shippingCost || 0,
      shipping_cost: shippingData?.shippingCost || 0,
      free_shipping_threshold:
        shippingData?.shippingOption?.free_threshold || 0,
    };
    await updateDoc(docRef, data);
  };

  const saveCartInfo = async (paymentIntent) => {
    try {
      const docRef = doc(
        db,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartID,
      );
      const data = {
        shipmentAddress: selectedAddress,
        isAnonymous: user.isAnonymous,
        walletDeduction: summary.paymentTotals.wallet,
      };
      if (paymentIntent) {
        data.stripePaymentIntentId = paymentIntent.id;
        data.paymentType = "Stripe";
      }
      await updateDoc(docRef, data);
    } catch (error) {
      console.error("Error during payment processing:", error);
      throw new Error(`Error saving cart info: ${error.message}`);
    }
  };

  const disabled =
    !selectedAddress ||
    !selectedShippingData ||
    isLoading ||
    !!cardError ||
    (!billingSameAsShipping && !billingAddressValid) ||
    (showAddNewForm && !shippingAddressValid) ||
    (selectedPaymentMethod === null &&
      summary.paymentTotals.cc > 1 &&
      !mountedCardElements.cardNumber);

  useEffect(() => {
    if (!refreshedAtTime) return () => {};
    if (!cartID) return () => {};

    const checkCart = async () => {
      const cartErrors = await validateCart(cartID);

      if (cartErrors.length > 0) {
        navigate("/cart");
        return;
      }
    };
    checkCart();
  }, [refreshedAtTime]);

  return (
    <div
      style={{
        backgroundColor: "white",
      }}
    >
      <Header search={false} />
      <div
        css={{
          maxWidth: "1140px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          paddingTop: 16,
        }}
      >
        <ShippingCheckoutBanner totalAmount={summary.subtotal} />

        {!user?.email && <CheckoutAuthBanner onSignInComplete={refresh} />}

        <div style={styles.checkoutContainer}>
          <h2 style={styles.orderTitle}>Shipping Details</h2>
          {!showAddNewForm && user?.email && (
            <div style={styles.address}>
              {addresses?.length > 0 ? (
                <>
                  <AddressBox addressObject={addresses[selectedAddressIndex]}>
                    <button
                      style={{ ...styles.linkButton }}
                      onClick={toggleAddressOptions}
                    >
                      <CreateOutlined
                        style={{
                          fontSize: ".8rem",
                          position: "relative",
                          top: 2,
                        }}
                      />{" "}
                      Change Address
                    </button>
                  </AddressBox>
                </>
              ) : (
                <button
                  style={styles.linkButton}
                  onClick={toggleAddressOptions}
                >
                  <CreateOutlined
                    style={{ fontSize: ".8rem", position: "relative", top: 2 }}
                  />{" "}
                  Please add an address
                </button>
              )}
            </div>
          )}
          {(showAddNewForm || !user?.email) && (
            <div>
              <AddressForm
                existingEmail={user?.email}
                onSave={saveAddress}
                onCancel={() => setShowAddNewForm(false)}
                showSaveButton={false}
                showCancelButton={addresses.length > 0 && user?.email}
                onChange={({ address, valid }) => {
                  setShippingAddress(address);
                  setShippingAddressValid(valid);
                }}
              />
            </div>
          )}

          {/* Shipping Speed Section */}
          <div>
            <h3 style={{ fontSize: 18 }}>Shipping Speed</h3>
            <p
              style={{
                fontSize: 13,
                color: "#666",
                lineHeight: 1.3,
                margin: "8px 0 16px 0",
              }}
            >
              Exact delivery timelines vary by address. Orders ship next
              business day except on holidays (*Note: Fri-Sun orders ship on
              Mon).
            </p>
            <ShippingOptions
              cartPrice={summary.subtotal}
              selectedSlug={selectedShippingData?.shippingOption?.slug}
              onChange={setSelectedShippingData}
            />
          </div>

          {summary.paymentTotals.cc > 1 && (
            <>
              {/* Payment Method Selection */}
              <PaymentMethodsContainer>
                <div>
                  <h3 style={{ fontSize: 18 }}>Payment Details</h3>
                  <HelpText>
                    All payment data is end-to-end encrypted <Lock />
                  </HelpText>
                </div>

                {/* Express Checkout Options - Apple Pay and Google Pay */}
                <ExpressCheckout
                  amount={summary.paymentTotals.cc}
                  onPaymentAuthorized={handleExpressPaymentAuthorization}
                  disabled={disabled || isLoading || isCheckoutLoading}
                  shippingAddress={selectedAddress}
                  savePaymentMethod={savePaymentMethod}
                />

                {/* Single CustomCardElement instance that stays mounted */}
                <div
                  style={{
                    display:
                      !user?.email ||
                      (user?.email &&
                        !loadingSavedMethods &&
                        (selectedPaymentMethod === null ||
                          savedPaymentMethods.length === 0))
                        ? "block"
                        : "none",
                    marginBottom:
                      user?.email &&
                      savedPaymentMethods.length > 0 &&
                      selectedPaymentMethod === null
                        ? "30px"
                        : "0",
                  }}
                >
                  <CustomCardElement
                    stripe={stripe}
                    onChange={handleCardElementChange}
                    onElementsReady={handleCardElementsReady}
                  />
                  {cardError && (
                    <div
                      style={{
                        color: "red",
                        marginTop: 8,
                        marginBottom: 8,
                        fontSize: 14,
                      }}
                    >
                      {cardError}
                    </div>
                  )}
                </div>

                {/* Authenticated user payment options */}
                {user?.email && (
                  <>
                    {loadingSavedMethods && (
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <GridLoader loading={true} />
                        <p>Loading payment methods...</p>
                      </div>
                    )}
                    {!loadingSavedMethods && savedPaymentMethods.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        {/* Saved Payment Methods */}
                        {savedPaymentMethods.map((pm) => (
                          <SavedPaymentMethod
                            key={pm.id}
                            pm={pm}
                            selectedPaymentMethod={selectedPaymentMethod}
                            onSelect={handlePaymentMethodSelect}
                          />
                        ))}

                        {/* Add New Card Option */}
                        <div
                          style={{
                            border:
                              selectedPaymentMethod === null
                                ? "2px solid #3B72FE"
                                : "1px solid #ddd",
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 12,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={() => handlePaymentMethodSelect(null)}
                        >
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              border:
                                selectedPaymentMethod === null
                                  ? "2px solid #3B72FE"
                                  : "2px solid #ccc",
                              marginRight: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {selectedPaymentMethod === null && (
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  backgroundColor: "#3B72FE",
                                }}
                              />
                            )}
                          </div>
                          <div style={{ fontWeight: "bold" }}>
                            Add new payment method
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Save payment method checkbox for authenticated users when using new card */}
                    {!loadingSavedMethods &&
                      (selectedPaymentMethod === null ||
                        savedPaymentMethods.length === 0) && (
                        <div style={{ margin: "16px 0" }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              fontSize: "15px",
                              gap: 8,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={savePaymentMethod}
                              onChange={(e) =>
                                setSavePaymentMethod(e.target.checked)
                              }
                              style={{
                                margin: 0,
                                accentColor: "black",
                                width: 18,
                                height: 18,
                                verticalAlign: "middle",
                              }}
                            />
                            <span
                              style={{
                                lineHeight: 1.2,
                                display: "inline-block",
                              }}
                            >
                              Save this payment method for future use
                            </span>
                          </label>
                        </div>
                      )}
                  </>
                )}
              </PaymentMethodsContainer>

              {user?.email && selectedPaymentMethod === null && (
                <>
                  <div style={{ margin: "24px 0 8px 0" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "15px",
                        gap: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={billingSameAsShipping}
                        onChange={(e) =>
                          setBillingSameAsShipping(e.target.checked)
                        }
                        style={{
                          margin: 0,
                          accentColor: "black",
                          width: 18,
                          height: 18,
                          verticalAlign: "middle",
                        }}
                      />
                      <span
                        style={{ lineHeight: 1.2, display: "inline-block" }}
                      >
                        Billing address same as shipping address
                      </span>
                    </label>
                  </div>
                  {!billingSameAsShipping && (
                    <div style={{ marginBottom: 24 }}>
                      <AddressForm
                        existingEmail={user?.email || selectedAddress.email}
                        onSave={(addr) => setBillingAddress(addr)}
                        showSaveButton={false}
                        showCancelButton={false}
                        showRecipientsName={false}
                        addressName="Billing Address"
                        onChange={({ address, valid }) => {
                          setBillingAddress(address);
                          setBillingAddressValid(valid);
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div>
          <div style={styles.summary}>
            <Summary summary={summary} appliedDiscount={appliedDiscount} />
            {/** */}
            <div ref={payButtonRef} style={{ height: 30 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              {isLoading || isCheckoutLoading === true ? (
                <FPButton
                  fontSize={"1.2rem"}
                  padding={"10px"}
                  disabled
                  $dark
                  style={{ maxWidth: 600, width: "100%" }}
                >
                  Loading...
                </FPButton>
              ) : summary.paymentTotals.cc === 0 ? (
                <FPButton
                  $dark
                  onClick={handleWalletOnlyPayment}
                  disabled={disabled}
                  fontSize={"1.2rem"}
                  padding={"10px"}
                  background={"#3b72fe"}
                  outlined={false}
                  textColor={"white"}
                  style={{ maxWidth: 600, width: "100%" }}
                >
                  {isLoading ? "Processing..." : "Place your order"}
                </FPButton>
              ) : (
                <FPButton
                  $dark
                  onClick={handleStripePaymentIntent}
                  disabled={disabled}
                  fontSize={"1.2rem"}
                  padding={"10px"}
                  background={"#3b72fe"}
                  outlined={false}
                  textColor={"white"}
                  style={{ maxWidth: 600, width: "100%" }}
                >
                  {isLoading ? "Processing..." : `Place your order`}
                </FPButton>
              )}
            </div>
          </div>
        </div>
      </div>
      <FAQ />
      <Footer />
      {showStickyFooter && (
        <StickyFooter>
          <div
            style={{
              display: "flex",
              margin: "10px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {isLoading || isCheckoutLoading === true ? (
              <FPButton
                fontSize={"1.2rem"}
                padding={"10px"}
                disabled
                $dark
                style={{ maxWidth: 600, width: "100%" }}
              >
                Loading...
              </FPButton>
            ) : summary.paymentTotals.cc === 0 ? (
              <FPButton
                $dark
                onClick={handleWalletOnlyPayment}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
                style={{ maxWidth: 600, width: "100%" }}
              >
                {isLoading ? "Processing..." : "Place your order"}
              </FPButton>
            ) : (
              <FPButton
                $dark
                onClick={handleStripePaymentIntent}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
                style={{ maxWidth: 600, width: "100%" }}
              >
                {isLoading ? "Processing..." : `Place your order`}
              </FPButton>
            )}
          </div>
        </StickyFooter>
      )}

      {/* Loading Overlay */}
      {isCheckoutLoading && (
        <div
          css={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            css={{
              width: 60,
              height: 60,
              border: "3px solid rgba(255, 255, 255, 0.3)",
              borderTop: "3px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: 20,
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <div
            css={{
              color: "white",
              fontSize: 18,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Processing your payment...
          </div>
          <div
            css={{
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: 14,
              textAlign: "center",
              marginTop: 8,
            }}
          >
            Please do not close this window
          </div>
        </div>
      )}

      {/* Address Modal */}
      <Sheet
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        snapPoints={[0.9]}
        disableDrag={true}
      >
        <Sheet.Container style={{}}>
          {" "}
          {/* Added position relative */}
          <div
            onClick={() => setOpen(false)}
            style={{
              cursor: "pointer",
              fontSize: "24px",
              fontWeight: "bold",
              position: "absolute" /* Changed from fixed to absolute */,
              right: 10,
              top: 5,
              zIndex: 10 /* Lower z-index as it's inside the container now */,
              width: 35,
              height: 35,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              lineHeight: "30px",
            }}
          >
            &times;
          </div>
          <Sheet.Content
            style={{
              overflowY: "auto",
              maxWidth: "1140px",
              margin: "0 auto",
              width: "100%",
            }}
          >
            <Sheet.Header>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: "20px 20px 10px 20px",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    margin: 0,
                    textAlign: "center",
                    width: "100%",
                    fontSize: "1.2rem",
                    fontWeight: 600,
                  }}
                >
                  Change Address
                </div>
                <div
                  style={{
                    marginTop: 15,
                    width: "100%",
                    fontWeight: 400,
                    fontSize: ".9rem",
                  }}
                >
                  Select the delivery address:
                </div>
              </div>
            </Sheet.Header>
            <div style={{ padding: "0 20px" }}>
              {addresses.map((addr, index) => (
                <div
                  key={index}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAddressSelect(index)}
                >
                  <AddressBox
                    key={index}
                    addressObject={addr}
                    selected={tempSelectedAddressIndex === index}
                  />
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    padding: "8px 16px",
                    border: "1px solid black",
                    marginTop: "10px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    borderRadius: "50px",
                  }}
                  onClick={handleAddNewAddress}
                >
                  <Add
                    style={{
                      fontSize: ".9rem",
                      position: "relative",
                      top: 2,
                      marginRight: 5,
                    }}
                  />
                  Add new address
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  style={{
                    backgroundColor: "black",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    width: "100%",
                    margin: "10px auto 30px auto",
                    cursor: "pointer",
                    fontSize: "16px",
                    borderRadius: "50px",
                    maxWidth: "340px",
                  }}
                  onClick={handleSaveAddressChange}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
      </Sheet>
    </div>
  );
};

const styles = {
  checkoutContainer: {
    width: "100%",
    padding: "0 20px",
    fontFamily: "'Inter', sans-serif",
  },
  orderTitle: { fontSize: "18px", marginBottom: "12px" },
  address: { marginBottom: "20px" },
  addressBox: {
    border: "1px solid #ddd",
    padding: "15px",
    borderRadius: "12px",
  },
  headerText: { fontSize: "14px", color: "gray", marginBottom: "10px" },
  nameText: { fontSize: "16px", fontWeight: "bold" },
  addressText: { fontSize: "14px" },
  linkButton: {
    fontSize: ".8rem",
    color: "black",
    cursor: "pointer",
    border: "1px solid black",
    borderRadius: "50px",
    padding: "5px 10px",
    marginTop: "10px",
    backgroundColor: "white",
  },
  total: {
    color: "black",
  },
  summary: {
    marginTop: "10px",
    padding: "10px 15px 40px",
    background: "rgba(59, 114, 254, 0.05)",
  },
};

export default Checkout;
